import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Alert,
  DialogActions,
  Box,
} from '@mui/material';
import { useGetEnterpriseCompaniesPlans } from '@features/payments/hooks/useGetEnterpriseCompaniesPlans';
import { Stack } from '@mui/system';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { Close, Done } from '@mui/icons-material';
import { ActivaAccountIcon } from '../../../vectors/index';
import { PLANS } from '@utils/pricing';
import CompaniesListWithPaymentSekelton from '@features/payments/components/PaymentCardDetail/CompaniesListWithPaymentSekelton';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';

type ReactivateSubscriptionModalProps = {
  handleClose: () => void;
  open: boolean;
};

const ReactivateSubscriptionModal: FC<ReactivateSubscriptionModalProps> = ({
  open,
  handleClose,
}) => {
  const { authData } = useContext(authContext);
  const billingCycle = authData?.user?.billing_cycle;
  const [errorText, setErrorText] = useState<string>('');
  const [selectedPlans, setSelectedPlans] = useState<
    { company_id: number; plan_id: number; new_plan_id: number }[]
  >([]);

  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });

  const { companiesPlansData, isLoadingCompaniesData } =
    useGetEnterpriseCompaniesPlans({ setErrorText });

  const handlePlanChange = (
    company_id: number,
    plan_id: number,
    plan: number,
  ) => {
    setSelectedPlans((prevState) => {
      const updatedPlans = prevState.map((p) =>
        p.company_id === company_id
          ? { ...p, plan_id: plan_id, new_plan_id: plan }
          : p,
      );

      if (!updatedPlans.find((p) => p.company_id === company_id)) {
        updatedPlans.push({ company_id, plan_id: plan_id, new_plan_id: plan });
      }
      return updatedPlans;
    });
  };

  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number }) => {
        if (data.status === 200) {
          toast.success('Subscription reactivated successfully');
          handleClose();
        }
      },
    };
    updatePaymentPlan(
      {
        billing_cycle: billingCycle ?? undefined,
        plan_ids: selectedPlans,
        upgrade_plan: true,
        switch: true,
        downgrade_plan: false,
      },
      options,
    );
  };

  const filteredData =
    companiesPlansData?.objects?.filter(
      (item: { is_company_paid: any }) => !item.is_company_paid,
    ) ?? [];
  useEffect(() => {
    if (filteredData) {
      if (filteredData?.length === 0) {
        setErrorText('No companies found');
      } else {
        setSelectedPlans(
          filteredData.map((item: { id: number; plan: number }) => ({
            company_id: item.id,
            plan_id: item.plan,
            new_plan_id: 1,
          })),
        );
      }
    }
  }, [filteredData?.length, companiesPlansData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ borderRadius: 3 }}
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <ActivaAccountIcon />
        <br />
        <Typography variant="h4">Activate Subscription</Typography>
        {filteredData?.length > 0 && !isLoadingCompaniesData && (
          <>
            <Typography align="center" sx={{ mt: 2 }}>
              Select a plan for each company
            </Typography>

            <Typography align="center" sx={{ mt: 2 }}>
              Total <b>{filteredData?.length}</b>
              {filteredData?.length === 1 ? ' Company' : ' Companies'}
            </Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {(errorText || filteredData?.length === 0) &&
          !isLoadingCompaniesData && (
            <Alert severity="error">{errorText || 'No companies found'}</Alert>
          )}
        {isLoadingCompaniesData ? (
          <Typography align="center" sx={{ mt: 2 }}>
            <CompaniesListWithPaymentSekelton skeletonArrayLenth={2} />
          </Typography>
        ) : filteredData?.length > 0 ? (
          <List>
            {filteredData.map(
              (item: { id: number; s_name: string, plan: number }, index: number) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: '100%' }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {index + 1}.
                      </Typography>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="bold">
                            {item.s_name}
                          </Typography>
                        }
                      />
                    </Stack>

                    <Box sx={{ width: '100%', mt: 1, ml: 3 }}>
                      <FormControl fullWidth>
                        <RadioGroup
                          row
                          value={
                            selectedPlans.find(
                              (plan) => plan.company_id === item.id,
                            )?.new_plan_id || 1
                          }
                          onChange={(e) =>
                            handlePlanChange(
                              item.id,
                              Number(item.plan),
                              Number(e.target.value),
                            )
                          }
                        >
                          <FormControlLabel
                            value={4}
                            control={<Radio />}
                            label={
                              <Typography variant="body2">
                                {PLANS.STARTER.NAME}
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value={1}
                            control={<Radio />}
                            label={
                              <Typography variant="body2">
                                {PLANS.SMARTER.NAME}
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio />}
                            label={
                              <Typography variant="body2">
                                {PLANS.PRO.NAME}
                              </Typography>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                </React.Fragment>
              ),
            )}
          </List>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', marginBottom: '20px' }}>
        <Button
          startIcon={<Close />}
          disabled={isUpdatingPaymentPlan}
          onClick={handleClose}
          variant="outlined"
          color="error"
          sx={{ width: '200px', fontSize: '1rem', padding: '10px' }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isUpdatingPaymentPlan}
          startIcon={<Done />}
          disabled={
            isUpdatingPaymentPlan ||
            isLoadingCompaniesData ||
            filteredData?.length === 0
          }
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ width: '200px', fontSize: '1rem', padding: '10px' }}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReactivateSubscriptionModal;